import { useTranslation } from 'react-i18next';

interface DesignCardProps {
  icon: string;
  titleKey: string;
  descKey: string;
  delay?: number;
}

function DesignCard({ icon, titleKey, descKey, delay = 0 }: DesignCardProps) {
  const { t } = useTranslation();

  return (
    <div
      className="group bg-background-100 rounded-xl p-6 md:p-8 hover:bg-background-50 transition-all duration-500 border border-background-200/50 hover:border-accent-200/50 hover:shadow-lg hover:shadow-accent-100/20"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 flex items-center justify-center mb-5 rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-600 group-hover:text-background-50 transition-all duration-500">
        <i className={`${icon} text-xl`} />
      </div>
      <h3 className="font-heading text-lg md:text-xl font-bold text-[#0A2D5E] mb-3 group-hover:text-[#0C3A75] transition-colors">
        {t(titleKey)}
      </h3>
      <p className="text-sm text-foreground-600 leading-relaxed">
        {t(descKey)}
      </p>
    </div>
  );
}

export default function DesignSection() {
  const { t } = useTranslation();

  const cards = [
    { icon: 'ri-building-line', titleKey: 'design.card1.title', descKey: 'design.card1.desc' },
    { icon: 'ri-heart-line', titleKey: 'design.card2.title', descKey: 'design.card2.desc' },
    { icon: 'ri-translate-2', titleKey: 'design.card3.title', descKey: 'design.card3.desc' },
    { icon: 'ri-map-pin-line', titleKey: 'design.card4.title', descKey: 'design.card4.desc' },
    { icon: 'ri-time-line', titleKey: 'design.card5.title', descKey: 'design.card5.desc' },
    { icon: 'ri-earth-line', titleKey: 'design.card6.title', descKey: 'design.card6.desc' },
  ];

  return (
    <section className="relative py-20 md:py-28 bg-background-50 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-accent-50/20 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs md:text-sm text-accent-500 font-medium tracking-widest uppercase mb-4">
            {t('design.label')}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2D5E] leading-tight mb-2">
            {t('design.title')}
          </h2>
          <h3 className="font-heading text-xl md:text-2xl lg:text-3xl font-semibold text-accent-500 leading-tight">
            {t('design.title2')}
          </h3>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {cards.map((card, index) => (
            <DesignCard
              key={card.titleKey}
              icon={card.icon}
              titleKey={card.titleKey}
              descKey={card.descKey}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
}